import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, ChevronDown } from 'lucide-react';
import { fetchSolutions } from '../utils/supabaseHelpers';

const ProblemFormModal = ({ problem, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    domain: 'mechanical',
    priority: 'medium',
    status: 'new',
    dueDate: '',
    notes: '',
    solutions: []
  });

  useEffect(() => {
    const loadProblemData = async () => {
      if (problem) {
        console.log('ProblemFormModal: Loading problem data for:', problem.id);
        
        // Load basic problem data - PRESERVE ALL ORIGINAL FIELDS
        const basicData = {
          ...problem,  // Keep all original fields including id
          dueDate: problem.due_date || problem.dueDate || '',
          solutions: []  // Start with empty solutions array
        };
        
        // Fetch solutions from database if editing existing problem
        if (problem.id) {
          try {
            const solutions = await fetchSolutions(problem.id);
            console.log('ProblemFormModal: Fetched solutions from DB:', solutions);
            // REPLACE solutions array completely (don't append)
            basicData.solutions = solutions.map(sol => ({
              description: sol.description,
              isChosen: sol.is_chosen
            }));
            console.log('ProblemFormModal: Mapped solutions for form:', basicData.solutions);
          } catch (error) {
            console.error('Failed to load solutions:', error);
            // Continue with empty solutions if fetch fails
          }
        }
        
        console.log('ProblemFormModal: Setting form data with solutions:', basicData.solutions);
        setFormData(basicData);
      } else {
        // Reset to initial state for new problem
        setFormData({
          title: '',
          description: '',
          domain: 'mechanical',
          priority: 'medium',
          status: 'new',
          dueDate: '',
          notes: '',
          solutions: []
        });
      }
    };
    
    loadProblemData();
  }, [problem]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleAddSolution = () => {
    setFormData({
      ...formData,
      solutions: [...formData.solutions, { description: '', isChosen: false }]
    });
  };

  const handleRemoveSolution = (index) => {
    setFormData({
      ...formData,
      solutions: formData.solutions.filter((_, i) => i !== index)
    });
  };

  const handleSolutionChange = (index, field, value) => {
    const updated = formData.solutions.map((sol, i) => {
      if (i === index) {
        return { ...sol, [field]: value };
      }
      return sol;
    });
    setFormData({ ...formData, solutions: updated });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.description.trim()) {
      alert('Title and Description are required');
      return;
    }
    
    console.log('ProblemFormModal: Submitting form with solutions:', formData.solutions);
    console.log('ProblemFormModal: Solutions count:', formData.solutions.length);
    onSubmit(formData);
  };

  return (
    <div className="modal-backdrop" onClick={onClose} data-testid="problem-form-modal">
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="p-6 border-b border-white/10">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold" data-testid="form-modal-title">
              {problem ? 'Edit Problem' : 'Create New Problem'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors"
              data-testid="form-modal-close-btn"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4 scrollbar-thin" style={{ maxHeight: 'calc(90vh - 180px)', overflowY: 'auto' }}>
          {/* Title */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Title <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              className="w-full glass-input"
              placeholder="Enter problem title"
              required
              data-testid="form-title-input"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Description <span className="text-red-400">*</span>
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              className="w-full glass-input resize-none"
              rows="4"
              placeholder="Enter detailed description"
              required
              data-testid="form-description-input"
            />
          </div>

          {/* Domain & Priority */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">
                Domain <span className="text-red-400">*</span>
              </label>
              <div className="relative">
                <select
                  name="domain"
                  value={formData.domain}
                  onChange={handleChange}
                  className="w-full glass-input cursor-pointer pr-10"
                  required
                  data-testid="form-domain-select"
                >
                  <option value="mechanical">Mechanical</option>
                  <option value="software">Software</option>
                  <option value="electronics">Electronics</option>
                  <option value="management">Management</option>
                </select>
                <ChevronDown size={16} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                Priority <span className="text-red-400">*</span>
              </label>
              <div className="relative">
                <select
                  name="priority"
                  value={formData.priority}
                  onChange={handleChange}
                  className="w-full glass-input cursor-pointer pr-10"
                  required
                  data-testid="form-priority-select"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="critical">Critical</option>
                </select>
                <ChevronDown size={16} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
              </div>
            </div>
          </div>

          {/* Status & Due Date */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">
                Status <span className="text-red-400">*</span>
              </label>
              <div className="relative">
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="w-full glass-input cursor-pointer pr-10"
                  required
                  data-testid="form-status-select"
                >
                  <option value="new">New</option>
                  <option value="under_discussion">Under Discussion</option>
                  <option value="solution_decided">Solution Decided</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                </select>
                <ChevronDown size={16} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Due Date</label>
              <input
                type="date"
                name="dueDate"
                value={formData.dueDate}
                onChange={handleChange}
                className="w-full glass-input"
                data-testid="form-due-date-input"
              />
            </div>
          </div>

          {/* Solutions */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium">Solutions</label>
              <button
                type="button"
                onClick={handleAddSolution}
                className="text-blue-400 hover:text-blue-300 text-sm flex items-center gap-1"
                data-testid="add-solution-btn"
              >
                <Plus size={16} />
                Add Solution
              </button>
            </div>
            <div className="space-y-3">
              {formData.solutions.map((solution, index) => (
                <div key={index} className="glass-card p-4" data-testid={`solution-form-${index}`}>
                  <textarea
                    value={solution.description}
                    onChange={(e) => handleSolutionChange(index, 'description', e.target.value)}
                    className="w-full glass-input resize-none mb-2"
                    rows="2"
                    placeholder="Enter solution description"
                    data-testid={`solution-description-${index}`}
                  />
                  <div className="flex items-center justify-between">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={solution.isChosen}
                        onChange={(e) => handleSolutionChange(index, 'isChosen', e.target.checked)}
                        className="w-4 h-4"
                        data-testid={`solution-chosen-${index}`}
                      />
                      <span className="text-sm">Chosen Solution</span>
                    </label>
                    <button
                      type="button"
                      onClick={() => handleRemoveSolution(index)}
                      className="text-red-400 hover:text-red-300"
                      data-testid={`solution-remove-${index}`}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium mb-2">Notes</label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              className="w-full glass-input resize-none"
              rows="3"
              placeholder="Enter additional notes"
              data-testid="form-notes-input"
            />
          </div>
        </form>

        {/* Actions */}
        <div className="p-6 border-t border-white/10 flex gap-3 justify-end">
          <button
            onClick={onClose}
            className="btn-secondary"
            data-testid="form-cancel-btn"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="btn-primary"
            data-testid="form-save-btn"
          >
            {problem ? 'Update' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProblemFormModal;
